/* @flow */
import {connect} from 'react-redux';
import {compose} from 'redux';
import {pick} from 'lodash';

import {
    filteredCountQuery,
    filteredSearchCountQuery
} from '../../queries/filteredCountQuery';
import {searchQueryVariables} from '../../queries/searchQuery';
import {ResultsParams} from '../../../server/graphql/params';

import type {State as ReduxState} from '../../reducers/index';
import type {FilteredCountQueryResponseData} from '../../queries/filteredCountQuery';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import FilterForm from '../FilterForm/FilterForm';
import MoreFiltersPanel from '../MoreFiltersPanel/MoreFiltersPanel';
import {searchRequestQueryVariables} from '../../queries/searchRequestQuery';
import withQuery from '../withQuery/withQuery';

export type CallbackProps = {
    filterGroups: FilterGroupType[],
    resultsCount?: ?number
};

type StateProps = {
    bounds: $PropertyType<$PropertyType<ReduxState, 'map'>, 'bounds'>,
    expandedSearchHasModifiedMapViewport: $PropertyType<
        $PropertyType<ReduxState, 'expandedGroups'>,
        'hasModifiedMapViewport'
    >,
    gtsQuery: $PropertyType<$PropertyType<ReduxState, 'system'>, 'gtsQuery'>,
    inputParams: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParams'
    >,
    inputParamsAreChanged: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParamsAreChanged'
    >,
    inputParamsAreUpdating: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParamsAreUpdating'
    >,
    params: $PropertyType<$PropertyType<ReduxState, 'searchParams'>, 'params'>,
    pathname: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'pathname'
    >
};

type WithFilteredCountDataProps = {
    data: FilteredCountQueryResponseData
};

type CallbackElementType = typeof MoreFiltersPanel | typeof FilterForm;

type Props = {
    children: (
        callbackProps: CallbackProps
    ) => React.Element<CallbackElementType>,
    searchResultsLoading: boolean
} & StateProps &
    WithFilteredCountDataProps;

export const FilteredCountDataProvider = ({children, data}: Props) => {
    const filterGroups =
        (data && data.results && data.results.filterGroups) || [];

    return children({
        resultsCount: data && data.results && data.results.resultCount,
        filterGroups
    });
};

FilteredCountDataProvider.displayName = 'FilteredCountDataProvider';

const mapStateToProps = ({
    system: {gtsQuery},
    expandedGroups: {
        hasModifiedMapViewport: expandedSearchHasModifiedMapViewport
    },
    map: {bounds},
    searchParams: {
        pathname,
        params,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreUpdating
    }
}: ReduxState): StateProps => ({
    gtsQuery,
    bounds,
    expandedSearchHasModifiedMapViewport,
    params,
    inputParams,
    inputParamsAreChanged,
    inputParamsAreUpdating,
    pathname
});

// We are going to cache parameters to the apollo HOC as a means to skip apollo request while maintaining state
// NOTE: `skip` resulted in the number of results being hidden temporarily
let variablesCached = null;

const makeSearchVariables = ({
    bounds,
    expandedSearchHasModifiedMapViewport,
    params,
    inputParams,
    inputParamsAreChanged,
    inputParamsAreUpdating,
    searchResultsLoading,
    pathname
}: Props) => {
    let variables = {params: {}};

    if (inputParamsAreUpdating || searchResultsLoading) {
        // use cached values if either a SearchQuery or FilterCountQuery is in flight
        variables = variablesCached || {params: {}};
    } else if (!inputParamsAreChanged) {
        // use cached search results if inputParams are equal to params
        variables = variablesCached = searchQueryVariables(params, pathname);
    } else {
        // make a query if we can't use cached search results
        variables = variablesCached = {
            params: {
                ...pick({...params, ...inputParams}, ResultsParams),
                pageSize: 0
            }
        };
    }

    /* If expanded search has modified the map viewport (expaned search groups has been uncollapsed)
    then we need update the bounding box to include the expanded searches for the filter count. Note here
    we do not use the bounds in the searchParams but from the transative map state */
    if (expandedSearchHasModifiedMapViewport) {
        variables = {
            ...variables,
            params: {...variables.params, ...bounds}
        };
    }

    return variables;
};

export const queryConfig = (props: Props) => {
    const variables = makeSearchVariables(props);

    return {
        document: props.gtsQuery
            ? filteredSearchCountQuery
            : filteredCountQuery,
        skip: props.searchResultsLoading && !variablesCached,
        options: {
            errorPolicy: 'all',
            variables: props.gtsQuery
                ? searchRequestQueryVariables(variables.params)
                : variables,
            ssr: false // don't need to ssr here, since we already fetched in the application route handler
        }
    };
};

export default compose(
    connect(mapStateToProps, {}),
    withQuery(queryConfig)
)(FilteredCountDataProvider);
